import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowDownToLine, ArrowUpFromLine, RotateCcw, ScanLine, Sliders, Trash } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate, fmtGNF, MOVEMENT_TYPES } from "@/lib/format";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { StockLosses } from "./StockLosses";
import { ABtn, AInput, ALabel, ASelect, Panel } from "./AdminLayout";

const TYPE_ICON = {
  entree: ArrowDownToLine,
  sortie: ArrowUpFromLine,
  ajustement: Sliders,
  retour: RotateCcw,
  endommage: Trash,
};

export default function AdminStock() {
  const [products, setProducts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ product_id: "", type: "entree", quantity: 1, reason: "", location: "" });
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);

  const load = async () => {
    try {
      const [p, a, m] = await Promise.all([
        api.get("/admin/products", { params: { search: search || undefined } }),
        api.get("/stock/alerts"),
        api.get("/stock/movements", { params: { limit: 60 } }),
      ]);
      setProducts(p.data);
      setAlerts(a.data);
      setMovements(m.data);
      setForm((f) => ({ ...f, product_id: f.product_id || p.data[0]?.id || "" }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/stock/movements", {
        product_id: form.product_id,
        type: form.type,
        quantity: Number(form.quantity),
        reason: form.reason,
        location: form.location || undefined,
      });
      toast.success("Mouvement de stock enregistré");
      setForm({ ...form, quantity: 1, reason: "", location: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-stock-page">
      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <Panel title="Mouvement de stock" subtitle="Entrées, sorties, retours et ajustements" testid="stock-movement-form">
          <div className="space-y-3">
            <ABtn variant="gold" onClick={() => setScanning(true)} className="w-full" data-testid="stock-scan-button">
              <ScanLine className="h-4 w-4" /> Scanner un code-barres
            </ABtn>
            <div>
              <ALabel>Produit *</ALabel>
              <ASelect value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="movement-product-select">
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.sku} — {p.name} (stock {p.stock})
                  </option>
                ))}
              </ASelect>
            </div>
            <div>
              <ALabel>Type de mouvement *</ALabel>
              <ASelect value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="movement-type-select">
                {MOVEMENT_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </ASelect>
            </div>
            <div>
              <ALabel>{form.type === "ajustement" ? "Nouvelle quantité exacte *" : "Quantité *"}</ALabel>
              <AInput type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="movement-quantity-input" />
            </div>
            <div>
              <ALabel>Motif</ALabel>
              <AInput value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Réception fournisseur, inventaire…" data-testid="movement-reason-input" />
            </div>
            <div>
              <ALabel>Emplacement (optionnel)</ALabel>
              <AInput value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="A1-R2-E03" data-testid="movement-location-input" />
            </div>
            <ABtn onClick={submit} disabled={busy || !form.product_id} className="w-full" data-testid="movement-submit-button">
              Enregistrer le mouvement
            </ABtn>
          </div>
          {scanning && (
            <BarcodeScanner
              onClose={() => setScanning(false)}
              onProduct={(p) => {
                setForm((f) => ({ ...f, product_id: p.id, location: p.location || "" }));
                setScannedProduct(p);
              }}
            />
          )}
          {scannedProduct && (
            <p className="mt-3 rounded-xl border border-emerald-900/60 bg-emerald-950/30 p-3 text-xs text-emerald-200" data-testid="scanned-product-banner">
              Produit sélectionné par scan : <strong>{scannedProduct.name}</strong> ({scannedProduct.sku}) · stock actuel {scannedProduct.stock}
            </p>
          )}
        </Panel>

        <Panel title="Alertes de stock faible" subtitle={`${alerts.length} produit(s) sous le seuil`} testid="stock-alerts-panel">
          {alerts.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune alerte : tous les stocks sont au-dessus du seuil.</p>
          ) : (
            <div className="space-y-2">
              {alerts.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 rounded-xl border border-amber-900/60 bg-amber-950/30 p-3" data-testid={`stock-alert-${p.sku}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{p.name}</p>
                    <p className="font-num text-[11px] text-amber-300">
                      {p.sku} · {p.location || "sans emplacement"}
                    </p>
                  </div>
                  <span className="font-num shrink-0 text-sm font-bold text-amber-300">
                    <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                    {p.stock} / seuil {p.low_stock_threshold}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel
        title="Inventaire entrepôt"
        subtitle={`${products.length} référence(s)`}
        actions={<AInput placeholder="Rechercher SKU ou nom…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" data-testid="stock-search-input" />}
        testid="stock-inventory-panel"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2">SKU</th>
                <th className="py-2">Produit</th>
                <th className="py-2">Emplacement</th>
                <th className="py-2 text-right">Disponible</th>
                <th className="py-2 text-right">Réservé</th>
                <th className="py-2 text-right">Vendu</th>
                <th className="py-2 text-right">Seuil</th>
                <th className="py-2 text-right">Valeur vente</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/60" data-testid={`stock-row-${p.sku}`}>
                  <td className="font-num py-2 text-slate-300">{p.sku}</td>
                  <td className="py-2 text-white">{p.name}</td>
                  <td className="font-num py-2 text-slate-400">{p.location || "—"}</td>
                  <td className={`font-num py-2 text-right font-semibold ${p.stock <= p.low_stock_threshold ? "text-rose-400" : "text-emerald-400"}`}>
                    {p.stock}
                  </td>
                  <td className="font-num py-2 text-right text-slate-300">{p.reserved_stock || 0}</td>
                  <td className="font-num py-2 text-right text-slate-300">{p.sold_stock || 0}</td>
                  <td className="font-num py-2 text-right text-slate-400">{p.low_stock_threshold}</td>
                  <td className="font-num py-2 text-right text-slate-300">{fmtGNF(p.price * p.stock)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <StockLosses products={products} onChanged={load} />

      <Panel title="Historique des mouvements" subtitle="Produit, quantité, type, responsable, date, motif" testid="stock-movements-panel">        <div className="space-y-2">
          {movements.length === 0 && <p className="text-sm text-slate-500">Aucun mouvement enregistré.</p>}
          {movements.map((m) => {
            const Icon = TYPE_ICON[m.type] || Sliders;
            return (
              <div key={m.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 p-3 text-sm" data-testid={`movement-row-${m.id}`}>
                <Icon className="h-4 w-4 shrink-0 text-gold" />
                <span className="min-w-0 flex-1 truncate text-white">{m.product_name}</span>
                <span className="font-num text-slate-300">
                  {m.type} · {m.quantity}
                </span>
                <span className="text-[11px] text-slate-400">{m.user_email}</span>
                <span className="text-[11px] text-slate-500">{fmtDate(m.created_at)}</span>
                {m.reason && <span className="w-full text-[11px] text-slate-400">Motif : {m.reason}</span>}
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
